import type { InvestigationImportResult, InvestigationMetadata } from "@/app/providers/services";
import { useInvestigationTransfer } from "@/features/investigations/transfer/useInvestigationTransfer";
import Button from "@imminently/decisively-ui/Button";
import FloatingPanel from "@imminently/decisively-ui/FloatingPanel";
import Icon from "@imminently/decisively-ui/Icon";
import ListNavigation from "@imminently/decisively-ui/ListNavigation";
import Popover from "@imminently/decisively-ui/Popover";
import TextInput from "@imminently/decisively-ui/TextInput";
import clsx from "clsx";
import type React from "react";
import { useState } from "react";
import { EditInvestigationDialog } from "./EditInvestigationDialog";

/**
 * A row in the selector, styled like the deci-ui select menu items used by the workspace and project
 * selectors. Rendered as a plain button (not `Select.MenuItem`) so the keyboard highlight that
 * `ListNavigation.Item` sets via `aria-selected` reaches the DOM.
 */
const MenuRow = ({
  id,
  selected = false,
  disabled = false,
  onSelect,
  children,
}: {
  id: string;
  selected?: boolean;
  disabled?: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) => (
  <ListNavigation.Item
    id={id}
    disabled={disabled}
    onSelect={onSelect}
    asChild
  >
    <button
      type="button"
      disabled={disabled}
      data-selected={selected ? "" : undefined}
      className={clsx("deci-select__menu-item min-h-9 text-sm", selected && "font-semibold")}
    >
      {children}
    </button>
  </ListNavigation.Item>
);

const Divider = () => <div className="-mx-2 my-2 h-px bg-border" />;

/**
 * The investigation toolbar's view selector: the current view's name opens a searchable list of
 * views, plus actions to add, edit (rename/delete), import and export views.
 */
export const InvestigationViewSelector = ({
  modelId,
  items,
  selectedId,
  currentName,
  dirty,
  creating,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  onImported,
}: {
  modelId: string;
  items: InvestigationMetadata[];
  selectedId: string | null;
  /** The current view's name, as shown in the toolbar. */
  currentName: string;
  dirty: boolean;
  creating: boolean;
  onSelect: (investigationId: string) => void;
  onCreate: () => void;
  onRename: (name: string) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
  onImported: (result: InvestigationImportResult) => void | Promise<void>;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(false);
  const transfer = useInvestigationTransfer({
    modelId,
    investigationId: selectedId ?? undefined,
    dirty,
    onImported,
  });

  const current = items.find((item) => item.id === selectedId);
  const canEdit = Boolean(current && !current.isDefault && current.id !== "default");
  const query = search.trim().toLowerCase();
  const visibleItems = query ? items.filter((item) => item.name.toLowerCase().includes(query)) : items;

  const setVisible = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  /** Close the popover before acting, so a dialog or file picker doesn't open behind it. */
  const act = (action: () => void) => () => {
    setVisible(false);
    action();
  };

  return (
    <>
      <Popover
        visible={open}
        onVisibleChange={setVisible}
        mode="click"
        interactable
        placement="bottom-start"
        autoFocus
      >
        <Popover.Reference asChild>
          <Button
            variant="ghost"
            size="sm"
            label="Switch view"
            className="min-w-0 max-w-full gap-2"
          >
            <span className="truncate text-base font-semibold">{currentName}</span>
            {/* `size` is applied inline, so it wins over the Button's 1.5rem svg rule. */}
            <Icon
              name="resize"
              size="1rem"
              className="shrink-0 text-muted-foreground"
            />
          </Button>
        </Popover.Reference>
        <Popover.Content asChild>
          <FloatingPanel className="w-80 p-2">
            <ListNavigation>
              <ListNavigation.Input asChild>
                <TextInput
                  autoFocus
                  placeholder="Find a view"
                  aria-label="Find a view"
                  startAdornment={<Icon name="search" />}
                  value={search}
                  onValueChange={setSearch}
                  fullWidth
                />
              </ListNavigation.Input>
              <div className="px-2 pb-1 pt-3 text-xs font-semibold uppercase text-muted-foreground">Views</div>
              <ListNavigation.List className="flex max-h-60 flex-col overflow-y-auto">
                {visibleItems.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground">No views match</div>
                ) : (
                  visibleItems.map((item) => {
                    const isCurrent = item.id === selectedId;
                    return (
                      <MenuRow
                        key={item.id}
                        id={`investigation-view-${item.id}`}
                        selected={isCurrent}
                        onSelect={act(() => onSelect(item.id))}
                      >
                        <Icon
                          name="check"
                          className={clsx("shrink-0", !isCurrent && "invisible")}
                        />
                        <span className="flex-1 truncate">{item.name}</span>
                        {item.isDefault ? (
                          <span className="rounded bg-secondary px-1.5 py-0.5 text-xs font-semibold text-muted-foreground">
                            Default
                          </span>
                        ) : null}
                      </MenuRow>
                    );
                  })
                )}
              </ListNavigation.List>
              <Divider />
              <MenuRow
                id="investigation-view-add"
                disabled={creating}
                onSelect={act(onCreate)}
              >
                <Icon name="add" />
                <span className="flex-1">Add new view</span>
              </MenuRow>
              {canEdit ? (
                <MenuRow
                  id="investigation-view-edit"
                  onSelect={act(() => setEditing(true))}
                >
                  <Icon name="edit" />
                  <span className="flex-1">Edit view…</span>
                </MenuRow>
              ) : null}
              <MenuRow
                id="investigation-view-import"
                onSelect={act(transfer.chooseImportFile)}
              >
                <Icon name="upload" />
                <span className="flex-1">Import…</span>
              </MenuRow>
              <Divider />
              <MenuRow
                id="investigation-view-export"
                disabled={!selectedId}
                onSelect={act(() => transfer.exportPages("page"))}
              >
                <Icon name="download" />
                <span className="flex-1">Export view</span>
                <span className="max-w-28 truncate text-xs font-normal text-muted-foreground">{currentName}</span>
              </MenuRow>
              <MenuRow
                id="investigation-view-export-all"
                onSelect={act(() => transfer.exportPages("all"))}
              >
                <Icon name="copy" />
                <span className="flex-1">Export all views</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {items.length === 1 ? "1 view" : `${items.length} views`}
                </span>
              </MenuRow>
            </ListNavigation>
          </FloatingPanel>
        </Popover.Content>
      </Popover>
      {transfer.elements}
      <EditInvestigationDialog
        open={editing}
        onOpenChange={setEditing}
        name={currentName}
        onRename={onRename}
        onDelete={onDelete}
      />
    </>
  );
};
